import React, { Component } from 'react';
import { FaTimesCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup } from 'components/buttons/Button';
import {
  addSelectedOrdersToBundle,
  createBundleDraftIsolated,
  getBundleColor,
  getBundleFontColor,
  moveOrdersMessage,
  OrderBundle,
  pickupOnTop,
  removeOrdersFromBundle,
  reorderBundleWithAlgorithm,
  reorderDraftNew,
} from '../../utils/bundleDraft';
import PlannerMapInterval, {
  isIntervalInList,
  isSameInterval,
} from './PlannerMapIntervals';
import './PlannerMapMenu.scss';
import {
  AutoBundlerProgressReport,
  runBundlingAlgorithm,
  subscribeToBundler,
} from '../../../../utils/bundlingService';
import dayjs, { Dayjs } from 'dayjs';
import {
  OrderListOrderClazz,
  OrderMonitor,
  Region,
  TimeInterval,
} from '../../../../types/orderTypes';
import {
  BundleJson,
  BundleStatus,
  CourierJson,
  ReorderMode,
} from '../../../../types/bundleTypes';
import PlannerMapDraftList from './PlannerMapDraftLists';
import Swal from 'sweetalert2';
import { filterNotInBundles } from '../../PlannerMap';
import { settings } from '../../../../settings';
import Fetch from 'utils/fetch';
import { Loader } from '../../../../components/Loader/Loader';
import {
  fetchOrdersBundles,
  fetchOrdersForMonitor,
} from '../../../../fetch/orderFetch';
import { toOrderInstructions } from '../../../../components/maps/BundleMap';
import AutoBundlerOptionsModal from './AutoBundlerOptionsModal';
import SimpleCheckBox from '../../../../components/inputs/Checkbox/SimpleCheckbox';

interface State {
  selectedIntervals: TimeInterval[];
  selectedSenders: Set<string>;
  operationInProgress: boolean;
  publishAllDraftsIsLoading: boolean;
  selectedBundlesDrafts: string[];
  showAutoBundlerOptionsModal: boolean;
  selectedOrdersAutoBundle: OrderListOrderClazz[];
  selectedAutoBundleAll: boolean;
  onlyScannedAtPartner: boolean;
  onlyNotScannedAtPartner: boolean;
  reorderHoverShift: boolean;
  onlyNotTempControlledOrders: boolean;
}

interface Props {
  middleText: string;
  date: Dayjs;
  orders: OrderListOrderClazz[];
  bundles: BundleJson[];
  selectedOrders: OrderListOrderClazz[];
  region: Region;
  couriers: CourierJson[];
  filteredOrders: OrderListOrderClazz[];
  onFilteredOrders: (filteredOrders: OrderListOrderClazz[]) => void;
  onOrderDeselect: (orderIds: string[]) => void;
  onFetchAll: (date: Dayjs) => Promise<void>;
  onOrdersRemoved: (bundleId: string, orderIds: string[]) => Promise<void>;
  onAutoBundlerProgress: (progress: AutoBundlerProgressReport) => void;
}

export default class PlannerMapMenu extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = this.blankState();
  }

  static defaultProps = {
    middleText: '',
  };

  blankState = (): State => {
    return {
      selectedIntervals: [],
      selectedSenders: new Set<string>(),
      operationInProgress: false,
      publishAllDraftsIsLoading: false,
      selectedBundlesDrafts: [],
      showAutoBundlerOptionsModal: false,
      selectedOrdersAutoBundle: [],
      selectedAutoBundleAll: false,
      onlyScannedAtPartner: false,
      onlyNotScannedAtPartner: false,
      reorderHoverShift: false,
      onlyNotTempControlledOrders: false,
    };
  };

  componentDidMount(): void {
    const { date, region } = this.props;

    this.initializeFilter();

    subscribeToBundler(
      region,
      'planner',
      () => {
        this.props.onFetchAll(date);
        this.setState({ operationInProgress: false });
      },
      error => {
        this.props.onAutoBundlerProgress({
          progress: 0,
          failed: true,
          error: 'Something unexpected occurred when running auto bundle',
          metadata: { region: region },
        });
        if (this.state.operationInProgress) {
          this.setState({ operationInProgress: false });
        }
      },
      progress => {
        this.setState({ operationInProgress: true });
        this.props.onAutoBundlerProgress(progress);
      }
    );
  }

  componentDidUpdate = (prevProps, prevState, snapshot) => {
    // Reset filter when changing dates or changing orders
    if (
      !this.props.date.isSame(prevProps.date) ||
      this.props.orders.length !== prevProps.orders.length ||
      !this.props.orders.every(
        (val, index) => val.id === prevProps.orders[index].id
      )
    ) {
      this.initializeFilter();
    }
    if (this.props.bundles.length !== prevProps.bundles.length) {
      const { orders } = this.props;
      const {
        selectedSenders,
        selectedIntervals,
        onlyScannedAtPartner,
        onlyNotScannedAtPartner,
        onlyNotTempControlledOrders,
      } = this.state;
      const filteredOrders = this.filterOrders(
        orders,
        selectedIntervals,
        selectedSenders,
        onlyScannedAtPartner,
        onlyNotScannedAtPartner,
        onlyNotTempControlledOrders
      );
      this.props.onFilteredOrders(filteredOrders);
    }
  };

  onSenderClick = (
    sender: { sender: string; address: string },
    selectedSenders: Set<string>,
    selectableOrders: OrderListOrderClazz[],
    timeIntervals: TimeInterval[],
    onlyScannedAtPartner,
    onlyNotScannedAtPartner,
    onlyNotTempControlledOrders
  ) => {
    const senderId = `${sender.sender}-${sender.address}`;
    if (selectedSenders.has(senderId)) {
      selectedSenders.delete(senderId);
    } else {
      selectedSenders.add(senderId);
    }
    this.setState({
      selectedSenders: selectedSenders,
    });
    const filteredOrders = this.filterOrders(
      selectableOrders,
      timeIntervals,
      selectedSenders,
      onlyScannedAtPartner,
      onlyNotScannedAtPartner,
      onlyNotTempControlledOrders
    );
    this.props.onFilteredOrders(filteredOrders);
  };

  filterOrders = (
    orders: OrderListOrderClazz[],
    selectedIntervals: TimeInterval[],
    selectedSenders: Set<string>,
    onlyScannedAtPartner: boolean,
    onlyNotScannedAtPartner: boolean,
    onlyNotTempControlledOrders: boolean
  ): OrderListOrderClazz[] => {
    return orders
      .filter(order => {
        return (
          selectedIntervals.length === 0 ||
          this.isWithinSelectedIntervals(order, selectedIntervals)
        );
      })
      .filter(order => {
        return (
          selectedSenders.size === 0 ||
          selectedSenders.has(
            `${order.senderName}-${order.originAddress.fullAddressString}`
          )
        );
      })
      .filter(order => {
        return onlyScannedAtPartner && !onlyNotScannedAtPartner
          ? order.scannedAtPartner
          : true;
      })
      .filter(order => {
        return onlyNotScannedAtPartner && !onlyScannedAtPartner
          ? !order.scannedAtPartner
          : true;
      })
      .filter(order => {
        if (onlyNotTempControlledOrders) {
          return !order.temperatureControlled;
        }
        return true;
      });
  };

  isWithinSelectedIntervals(
    order: OrderListOrderClazz,
    selectedIntervals: TimeInterval[]
  ): boolean {
    const orderStart = dayjs(order.deliverFrom);
    const orderEnd = dayjs(order.deliverUntil);

    return selectedIntervals.some(interval => {
      return orderStart.isSame(interval.from) && orderEnd.isSame(interval.to);
    });
  }

  initializeFilter = () => {
    const newState = this.blankState();
    const {
      selectedIntervals,
      selectedSenders,
      onlyScannedAtPartner,
      onlyNotScannedAtPartner,
      onlyNotTempControlledOrders,
    } = newState;

    this.setState(newState);
    const filteredOrders = this.filterOrders(
      this.props.orders,
      selectedIntervals,
      selectedSenders,
      onlyScannedAtPartner,
      onlyNotScannedAtPartner,
      onlyNotTempControlledOrders
    );
    this.props.onFilteredOrders(filteredOrders);
  };

  autoBundleSelectedOrders() {
    const { selectedOrders } = this.props;
    this.setState({
      showAutoBundlerOptionsModal: true,
      selectedOrdersAutoBundle: selectedOrders,
      selectedAutoBundleAll: false,
    });
  }

  autoBundleAllOrders() {
    const {
      selectedIntervals,
      selectedSenders,
      onlyScannedAtPartner,
      onlyNotScannedAtPartner,
      onlyNotTempControlledOrders,
    } = this.state;
    const { bundles, orders } = this.props;
    const ordersNotInBundle = filterNotInBundles(
      this.filterOrders(
        orders,
        selectedIntervals,
        selectedSenders,
        onlyScannedAtPartner,
        onlyNotScannedAtPartner,
        onlyNotTempControlledOrders
      ),
      bundles
    );

    if (ordersNotInBundle.length === 0) {
      Swal.fire({
        title: `There are no orders to bundle.`,
        icon: 'info',
      });
      return;
    }
    this.setState({
      showAutoBundlerOptionsModal: true,
      selectedOrdersAutoBundle: ordersNotInBundle,
      selectedAutoBundleAll: true,
    });
  }

  async runAutoBundling(
    timePerStop: number,
    hubDeparture: string,
    createBundles: boolean
  ) {
    const orders =
      !createBundles && this.state.selectedAutoBundleAll
        ? this.props.orders
        : this.state.selectedOrdersAutoBundle;
    const { date, region } = this.props;

    const bundlingStarted = await runBundlingAlgorithm(
      orders,
      date,
      region,
      timePerStop,
      hubDeparture,
      createBundles,
      'planner',
      () => {
        this.props.onFetchAll(date);
        this.setState({ operationInProgress: false });
      },
      error => {
        this.props.onAutoBundlerProgress({
          progress: 0,
          failed: true,
          error: 'Something unexpected occurred when running auto bundle',
          metadata: { region: region },
        });
        if (this.state.operationInProgress) {
          this.setState({ operationInProgress: false });
        }
      },
      progress => {
        if (createBundles) {
          this.props.onAutoBundlerProgress(progress);
        }
      }
    );

    if (bundlingStarted && createBundles) {
      this.setState({ operationInProgress: true });

      this.props.onAutoBundlerProgress({
        progress: 0,
        failed: false,
        metadata: {
          region: region,
        },
      });
    }
  }

  selectBundleDraft = (bundleDraftId: string) => {
    this.setState({
      selectedBundlesDrafts: [
        ...this.state.selectedBundlesDrafts,
        bundleDraftId,
      ],
    });
  };

  deselectBundleDraft = (bundleDraftId: string) => {
    const { selectedBundlesDrafts } = this.state;
    const index = selectedBundlesDrafts.findIndex(
      selectedBundleDraft => selectedBundleDraft === bundleDraftId
    );

    this.setState({
      selectedBundlesDrafts: [
        ...selectedBundlesDrafts.slice(0, index),
        ...selectedBundlesDrafts.slice(index + 1),
      ],
    });
  };

  selectOrDeselectAllDrafts = (allBundlesIsSelected: boolean) => {
    if (allBundlesIsSelected) {
      this.setState({ selectedBundlesDrafts: [] });
    } else {
      const allBundleDraftIds = this.props.bundles
        .filter(bundle => bundle.status === BundleStatus.Draft)
        .map(bundle => bundle.id);

      this.setState({ selectedBundlesDrafts: allBundleDraftIds });
    }
  };

  publishSelectedDrafts = async () => {
    const { selectedBundlesDrafts } = this.state;

    this.setState({ publishAllDraftsIsLoading: true });

    const response = await Fetch.put({
      url: `${settings.newApiUrl}/bundle/publish`,

      body: {
        bundleIds: selectedBundlesDrafts,
      },
    });

    if (response.ok) {
      this.setState({
        publishAllDraftsIsLoading: false,
        selectedBundlesDrafts: [],
      });
      this.props.onFetchAll(this.props.date);
    }
  };

  deleteMultipleDrafts = async () => {
    const { selectedBundlesDrafts } = this.state;

    const requests = selectedBundlesDrafts.map(async bundleDraftId => {
      return Fetch.delete({
        url: `${settings.newApiUrl}/bundle/${bundleDraftId}`,
      });
    });

    const responses = await Promise.all(requests);
    if (responses.some(response => !response.ok)) {
      alert('Something wrong happened');
    }
    this.setState({ selectedBundlesDrafts: [] });
    this.props.onFetchAll(this.props.date);
  };

  addSelectedOrdersToBundleWrapper = (
    bundle: BundleJson,
    selectedOrders: OrderListOrderClazz[]
  ) => {
    const { onFetchAll, date } = this.props;
    addSelectedOrdersToBundle(bundle.id, selectedOrders).then(
      () => {
        onFetchAll(date);
        this.setState({ operationInProgress: false });
      },
      (error: Error) => {
        Swal.fire('Could not add order to bundle', error.message, 'error');
        this.setState({ operationInProgress: false });
      }
    );
  };

  async getOrdersAlreadyInBundle(
    orders: OrderListOrderClazz[]
  ): Promise<OrderBundle[]> {
    const orderBundleList = await fetchOrdersBundles(
      orders.map(order => order.id)
    );
    return orderBundleList.filter(ob => !!ob.bundle);
  }

  async createDraftBundle(orders: OrderListOrderClazz[]): Promise<BundleJson> {
    const { region, onFetchAll, date } = this.props;
    try {
      const bundleCreated = await createBundleDraftIsolated(orders, region);
      let fetchedOrders: OrderMonitor[];
      try {
        fetchedOrders = await fetchOrdersForMonitor(
          bundleCreated.instructions.map(instruction => instruction.orderId)
        );
      } catch {
        fetchedOrders = [];
      }
      const orderInstruction = toOrderInstructions(
        bundleCreated,
        fetchedOrders
      );
      await reorderDraftNew(
        bundleCreated.id,
        pickupOnTop(orderInstruction).map(
          orderInstruction => orderInstruction.instruction
        )
      );
      onFetchAll(date);
      return bundleCreated;
    } catch (error) {
      Swal.fire('Could not create new bundle', error?.message || '', 'error');
      return null;
    }
  }

  createBundle = async (
    selectedOrders: OrderListOrderClazz[],
    reorderBundleAfterCreation: boolean,
    reorderMode: ReorderMode = ReorderMode.OpenRoute
  ): Promise<void> => {
    this.setState({ operationInProgress: true });
    const ordersAlreadyInBundle = await this.getOrdersAlreadyInBundle(
      selectedOrders
    );
    if (ordersAlreadyInBundle.length) {
      Swal.fire({
        html: moveOrdersMessage(ordersAlreadyInBundle, null),
        title: 'Move orders to new bundle?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Move orders',
        cancelButtonText: 'Cancel',
      })
        .then(async result => {
          if (result.value) {
            await removeOrdersFromBundle(
              ordersAlreadyInBundle.map(ob => ob.orderId)
            );
            this.createDraftBundle(selectedOrders);
          }
          this.setState({ operationInProgress: false });
        })
        .catch(error => {
          Swal.fire(
            'Could not move orders to the new bundle',
            error.message,
            'error'
          );
          this.setState({ operationInProgress: false });
        });
    } else {
      const bundle = await this.createDraftBundle(selectedOrders);

      if (bundle && reorderBundleAfterCreation) {
        await reorderBundleWithAlgorithm(
          reorderMode,
          bundle.id,
          () => {
            this.setState({ operationInProgress: false });
            const { onFetchAll, date } = this.props;
            onFetchAll(date);
          },
          error => {
            this.setState({ operationInProgress: false });
            Swal.fire({
              title: `Something unexpected occurred when re-ordering bundle. Error response: ${JSON.stringify(
                error
              )}`,
              icon: 'error',
            });
          }
        );
      } else {
        this.setState({ operationInProgress: false });
      }
    }
  };

  addOrMoveOrders = async (
    selectedOrders: OrderListOrderClazz[],
    bundle: BundleJson
  ) => {
    this.setState({ operationInProgress: true });
    const ordersAlreadyInBundle = await this.getOrdersAlreadyInBundle(
      selectedOrders
    );
    if (ordersAlreadyInBundle.length) {
      Swal.fire({
        html: moveOrdersMessage(ordersAlreadyInBundle, null),
        title: 'Move orders to another bundle?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Move orders',
        cancelButtonText: 'Cancel',
      })
        .then(async result => {
          if (result.value) {
            await removeOrdersFromBundle(
              ordersAlreadyInBundle.map(ob => ob.orderId)
            );
            this.addSelectedOrdersToBundleWrapper(bundle, selectedOrders);
          }
          this.setState({ operationInProgress: false });
        })
        .catch(error => {
          Swal.fire(
            'Could not move orders to existing bundle',
            error.message,
            'error'
          );
          this.setState({ operationInProgress: false });
        });
    } else {
      this.addSelectedOrdersToBundleWrapper(bundle, selectedOrders);
    }
  };

  render() {
    const bundleStatues = new Set(
      this.props.bundles.map(bundle => bundle.status)
    );
    const showingOnlyDrafts =
      this.props.bundles.length === 0 ||
      (bundleStatues.has(BundleStatus.Draft) && bundleStatues.size === 1);
    const {
      selectedIntervals,
      selectedSenders,
      publishAllDraftsIsLoading,
      selectedBundlesDrafts,
      operationInProgress,
      showAutoBundlerOptionsModal,
      onlyScannedAtPartner,
      onlyNotScannedAtPartner,
      onlyNotTempControlledOrders,
    } = this.state;
    const {
      middleText,
      bundles,
      orders,
      selectedOrders,
      onOrderDeselect,
      region,
      filteredOrders,
    } = this.props;
    const hasSelectedOrders = selectedOrders.length > 0;
    const bundleDrafts = bundles.filter(b => b.status === BundleStatus.Draft);
    const allDraftBundlesSelected =
      selectedBundlesDrafts.length === bundleDrafts.length &&
      selectedBundlesDrafts.length !== 0;
    const filteredBundlesByFilteredOrders = filteredOrders
      ? Array.from(
          new Set(
            filteredOrders
              .map(order => order.bundleId)
              .filter(bundleId => bundleId)
          )
        )
      : null;
    return (
      <div>
        <div className="planner-map__filter-menu-header">
          <h4>Filter</h4>
          <div>{middleText}</div>
          <Button
            title="Clear filters"
            type="super-slim"
            onClick={event => this.initializeFilter()}
          />
        </div>
        <div className="filter-menu__select-time-slot">
          {orders.length === 0 && (
            <div>No orders that are not in published bundles</div>
          )}
          <PlannerMapInterval
            onTimeIntervalSelected={interval => {
              const isSelected = isIntervalInList(selectedIntervals, interval);
              let newSelectedIntervals = selectedIntervals;
              if (isSelected) {
                newSelectedIntervals = selectedIntervals.filter(
                  selected => !isSameInterval(selected, interval)
                );
              } else {
                newSelectedIntervals.push(interval);
              }

              this.setState({
                selectedIntervals: newSelectedIntervals,
              });
              const filteredOrders = this.filterOrders(
                orders,
                newSelectedIntervals,
                selectedSenders,
                onlyScannedAtPartner,
                onlyNotScannedAtPartner,
                onlyNotTempControlledOrders
              );
              this.props.onFilteredOrders(filteredOrders);
            }}
            selectedTimeIntervals={selectedIntervals}
            orders={orders}
            bundles={bundles}
          />
          <div style={{ height: 10 }} />
          <div>
            <div>
              <SimpleCheckBox
                onChange={() => {
                  const newOnlyScannedAtPartner = !onlyScannedAtPartner;
                  this.setState({
                    onlyScannedAtPartner: newOnlyScannedAtPartner,
                  });
                  const filteredOrders = this.filterOrders(
                    orders,
                    selectedIntervals,
                    selectedSenders,
                    newOnlyScannedAtPartner,
                    onlyNotScannedAtPartner,
                    onlyNotTempControlledOrders
                  );
                  this.props.onFilteredOrders(filteredOrders);
                }}
                selected={onlyScannedAtPartner}
                label={'Only with partner scan'}
                labelId={'withPartnerScan'}
              />
            </div>
            <div>
              <SimpleCheckBox
                onChange={() => {
                  const newOnlyNotScannedAtPartner = !onlyNotScannedAtPartner;
                  this.setState({
                    onlyNotScannedAtPartner: newOnlyNotScannedAtPartner,
                  });
                  const filteredOrders = this.filterOrders(
                    orders,
                    selectedIntervals,
                    selectedSenders,
                    onlyScannedAtPartner,
                    newOnlyNotScannedAtPartner,
                    onlyNotTempControlledOrders
                  );
                  this.props.onFilteredOrders(filteredOrders);
                }}
                selected={onlyNotScannedAtPartner}
                label={'Only without partner scan'}
                labelId={'withoutPartnerScan'}
              />
            </div>
            <div>
              <SimpleCheckBox
                onChange={() => {
                  const newOnlyNotTempControlledOrders =
                    !onlyNotTempControlledOrders;
                  this.setState({
                    onlyNotTempControlledOrders: newOnlyNotTempControlledOrders,
                  });
                  const filteredOrders = this.filterOrders(
                    orders,
                    selectedIntervals,
                    selectedSenders,
                    onlyScannedAtPartner,
                    onlyNotScannedAtPartner,
                    newOnlyNotTempControlledOrders
                  );
                  this.props.onFilteredOrders(filteredOrders);
                }}
                selected={onlyNotTempControlledOrders}
                label={'Orders without temp control'}
                labelId={'withTempControl'}
              />
            </div>
          </div>
          <div>Pickups </div>
          <div className="filter-menu__select-pickup">
            {orders
              .reduce((currentList, currentOrder) => {
                const existingPos = currentList.findIndex(
                  item =>
                    item.sender === currentOrder.senderName &&
                    item.address ===
                      currentOrder.originAddress.fullAddressString
                );
                if (existingPos !== -1) {
                  currentList[existingPos].count++;
                } else {
                  currentList.push({
                    sender: currentOrder.senderName,
                    address: currentOrder.originAddress.fullAddressString,
                    count: 1,
                  });
                }
                return currentList;
              }, [])
              .map(sender => (
                <div
                  className="select-pickup__line"
                  key={`${sender.sender}-${sender.address}`}
                  onClick={() => {
                    this.onSenderClick(
                      sender,
                      selectedSenders,
                      orders,
                      selectedIntervals,
                      onlyScannedAtPartner,
                      onlyNotScannedAtPartner,
                      onlyNotTempControlledOrders
                    );
                  }}
                  style={
                    selectedSenders.has(`${sender.sender}-${sender.address}`)
                      ? { backgroundColor: '#dedede' }
                      : {}
                  }
                >
                  {sender.count}: {sender.sender} - {sender.address}
                </div>
              ))}
          </div>
          <div style={{ height: 20 }} />
          <h4>
            Selected orders ({selectedOrders.length}){' '}
            <Button
              title={<FaTimesCircle />}
              type="super-slim"
              onClick={() => {
                onOrderDeselect(selectedOrders.map(order => order.id));
              }}
            />
          </h4>
          {selectedOrders.length === 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingBottom: 2,
                backgroundColor: '#f0f0f0',
              }}
            >
              No selected orders
            </div>
          )}
          {selectedOrders.map((selectedOrder, idx) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingBottom: 2,
                backgroundColor: idx % 2 ? '#f8f8f8' : '#f0f0f0',
              }}
              key={selectedOrder.id}
            >
              <div>
                <Link
                  to={`/orders/${selectedOrder.id}`}
                  className="link__clean"
                >
                  {selectedOrder.id}
                </Link>
                <span
                  style={{
                    paddingLeft: 2,
                  }}
                >
                  {selectedOrder.pickupName}
                </span>
              </div>
              <div>
                <span
                  style={{
                    paddingRight: 2,
                  }}
                >
                  {dayjs(selectedOrder.deliverFrom).format('HH:mm')}-
                  {dayjs(selectedOrder.deliverUntil).format('HH:mm')}
                </span>
                <Button
                  title={<FaTimesCircle />}
                  type="super-slim"
                  onClick={() => onOrderDeselect([selectedOrder.id])}
                />
              </div>
            </div>
          ))}

          {bundles.length > 0 && (
            <div style={{ fontSize: '1.2rem', marginTop: 6 }}>
              Add selected to bundle:
            </div>
          )}
          <ButtonGroup className="planner-map__filter-bundle-buttons">
            {(filteredBundlesByFilteredOrders
              ? bundles.filter(bundle =>
                  filteredBundlesByFilteredOrders.includes(bundle.id)
                )
              : bundles
            ).map(bundleDraft => (
              <Button
                key={bundleDraft.id}
                title={bundleDraft.id}
                style={{
                  backgroundColor: getBundleColor(bundleDraft.id),
                  color: getBundleFontColor(bundleDraft.id),
                }}
                type="super-slim"
                disabled={!hasSelectedOrders || operationInProgress}
                onClick={() =>
                  this.addOrMoveOrders(selectedOrders, bundleDraft)
                }
              />
            ))}
          </ButtonGroup>
          <div className="filter-menu__separator" />

          <h4>Bundles</h4>
          <div className="planner-map__filter-bundle-actions">
            <ButtonGroup>
              <Button
                title={allDraftBundlesSelected ? 'Unselect' : 'Select'}
                type="slim"
                onClick={() => {
                  this.selectOrDeselectAllDrafts(allDraftBundlesSelected);
                }}
                disabled={bundleDrafts.length === 0}
              />
              <Button
                title={
                  hasSelectedOrders
                    ? `Auto bundle ${selectedOrders.length}`
                    : 'Auto bundle all'
                }
                type="slim"
                disabled={operationInProgress || !showingOnlyDrafts}
                onClick={() => {
                  if (hasSelectedOrders) {
                    this.autoBundleSelectedOrders();
                  } else {
                    this.autoBundleAllOrders();
                  }
                }}
              />
              <Button
                title="Create"
                type="slim"
                onClick={() => this.createBundle(selectedOrders, false)}
                disabled={
                  !hasSelectedOrders ||
                  operationInProgress ||
                  !showingOnlyDrafts
                }
              />
              <Button
                title="Create & Reorder"
                type="slim"
                className={
                  this.state.reorderHoverShift ? 'hover-shift' : undefined
                }
                onClick={() =>
                  this.createBundle(
                    selectedOrders,
                    true,
                    this.state.reorderHoverShift
                      ? ReorderMode.AutoBundler
                      : ReorderMode.OpenRoute
                  )
                }
                disabled={
                  !hasSelectedOrders ||
                  operationInProgress ||
                  !showingOnlyDrafts
                }
                onMouseEnter={e =>
                  e.shiftKey && this.setState({ reorderHoverShift: true })
                }
                onMouseLeave={() => this.setState({ reorderHoverShift: false })}
              />
              <Button
                title="Publish"
                type="slim"
                className="success"
                onClick={() => {
                  this.publishSelectedDrafts();
                }}
                loading={publishAllDraftsIsLoading}
                disabled={
                  publishAllDraftsIsLoading ||
                  selectedBundlesDrafts.length === 0
                }
              />
              <Button
                title="Delete"
                type="slim"
                className="danger"
                onClick={async () => {
                  const result = await Swal.fire({
                    title: 'Danger zone!',
                    text: `Are you sure you want to delete the following drafts?
                    ${selectedBundlesDrafts.join(', ')}`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Delete drafts',
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#ccc',
                  });
                  if (result && result.value) {
                    await this.deleteMultipleDrafts();
                  }
                }}
                disabled={
                  publishAllDraftsIsLoading ||
                  selectedBundlesDrafts.length === 0
                }
              />
            </ButtonGroup>
          </div>
          {(operationInProgress || publishAllDraftsIsLoading) && (
            <Loader type="bounce" size="medium" />
          )}
        </div>
        <div style={{ height: 10 }} />
        <PlannerMapDraftList
          orders={orders}
          bundles={bundles}
          onSavedModification={() => this.props.onFetchAll(this.props.date)}
          onOrdersRemoved={this.props.onOrdersRemoved}
          selectedBundlesDrafts={this.state.selectedBundlesDrafts}
          onDeselectBundleDraft={bundleId => this.deselectBundleDraft(bundleId)}
          onSelectBundleDraft={bundleId => this.selectBundleDraft(bundleId)}
          region={region}
          selectedDate={this.props.date}
        />
        {showAutoBundlerOptionsModal && (
          <AutoBundlerOptionsModal
            region={region}
            onCloseModal={() =>
              this.setState({
                showAutoBundlerOptionsModal: false,
                selectedOrdersAutoBundle: [],
              })
            }
            onOptionsSelected={async (
              timePerStop,
              hubDeparture,
              createBundles
            ) => {
              this.setState({ showAutoBundlerOptionsModal: false });
              this.runAutoBundling(timePerStop, hubDeparture, createBundles);
            }}
          />
        )}
      </div>
    );
  }
}
